'use client';
import { useScrollToItemOnHash } from '@vs-shop/headless/useScrollToItemOnHash';
import { updateUrlOnLinkClick } from '../../../shared/utils/updateUrlOnLinkClick';
import { Chunk } from 'effect';

import { pageParamName } from '../../../config/urlParams';

import { ArticleCardVariant } from '@vs-shop/commerce-ui/articleCard';

import ArticlePreview from '../../server/page/article/overview/ArticlePreview';
import ArticlesEmpty from '../../server/page/article/overview/ArticlesEmpty';
import ArticleSkeleton from '../../server/page/article/overview/ArticleSkeleton';

import { fetchArticlesPageAtom } from '@vs-shop/engine/atom/articles/fetchArticlesPage';
import type { AuthorId, CategoryId } from '@vs-shop/schemas/branded/ContentSchema';
import { loadMorePagesAtom } from '@vs-shop/engine/atom/products/pagination';
import type { ArticleIndex } from '../../../effect/schema/openapi/backend';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomValue } from '@effect/atom-react';

type Props = {
  currentPage: number;
  itemsPerPage: number;
  section: string | undefined;
  categoryId: CategoryId | undefined;
  authorId: AuthorId | undefined;
};

export default function ArticlesGrid({
  currentPage,
  itemsPerPage,
  section,
  categoryId,
  authorId,
}: Props) {
  useScrollToItemOnHash();

  const loadMorePages = useAtomValue(loadMorePagesAtom);

  const startPage = currentPage - loadMorePages;
  const endPage = currentPage;

  const articlesPerPageResponse = useAtomValue(
    fetchArticlesPageAtom(startPage, itemsPerPage, section, categoryId, authorId)
  );

  const skeletonCount = 3;

  return AsyncResult.builder(articlesPerPageResponse)
    .onInitial(() => (
      <div className="space-y-10 xl:space-y-16">
        {Array.from({ length: skeletonCount }, (_, index) => {
          if (index === 0) {
            return (
              <ArticleSkeleton
                key={`skeleton-${String(index)}`}
                variant={ArticleCardVariant.ROW_LEAD}
              />
            );
          }
          return (
            <ArticleSkeleton key={`skeleton-${String(index)}`} variant={ArticleCardVariant.ROW} />
          );
        })}
      </div>
    ))
    .onSuccess((success) => {
      const totalMembers = success['hydra:totalItems'];

      if (totalMembers === 0) {
        return <ArticlesEmpty />;
      }

      return (
        <div className="space-y-10 xl:space-y-16">
          {Chunk.range(startPage, endPage).pipe(
            Chunk.map((page) => (
              <ArticlesGridPerPage
                key={page}
                page={page}
                itemsPerPage={itemsPerPage}
                section={section}
                categoryId={categoryId}
                authorId={authorId}
              />
            )),
            Chunk.toReadonlyArray
          )}
        </div>
      );
    })
    .render();
}

type ArticlesGridPerPageProps = {
  page: number;
  itemsPerPage: number;
  section: string | undefined;
  categoryId: CategoryId | undefined;
  authorId: AuthorId | undefined;
};

function ArticlesGridPerPage({
  page,
  itemsPerPage,
  section,
  categoryId,
  authorId,
}: ArticlesGridPerPageProps) {
  const articlesPerPageResponse = useAtomValue(
    fetchArticlesPageAtom(page, itemsPerPage, section, categoryId, authorId)
  );

  const currentLoadMorePages = useAtomValue(loadMorePagesAtom);

  const handleArticleNavigation = (article: ArticleIndex) => {
    if (currentLoadMorePages === 0) return;
    if (!article.slug) return;
    updateUrlOnLinkClick(page, pageParamName, article.slug);
  };

  // Show skeleton while loading individual pages
  if (AsyncResult.isInitial(articlesPerPageResponse)) {
    return (
      <div className="space-y-10 xl:space-y-16">
        {Array.from({ length: itemsPerPage }, (_, index) => {
          if (page === 1 && index === 0) {
            return (
              <ArticleSkeleton
                key={`page-${String(page)}-skeleton-${String(index)}`}
                variant={ArticleCardVariant.ROW_LEAD}
              />
            );
          }
          return (
            <ArticleSkeleton
              key={`page-${String(page)}-skeleton-${String(index)}`}
              variant={ArticleCardVariant.ROW}
            />
          );
        })}
      </div>
    );
  }

  const articles = AsyncResult.match(articlesPerPageResponse, {
    onInitial: () => [],
    onFailure: () => [],
    onSuccess: ({ value }) => value['hydra:member'],
  });

  if (articles.length === 0) {
    return null;
  }

  const articleFirst = articles[0];
  const articlesAfterFirst = articles.slice(1);

  return (
    <>
      {page === 1 && articleFirst && (
        <div
          data-slot="article-card-place"
          data-hash-identifier={articleFirst.slug ?? undefined}
          onClick={() => {
            handleArticleNavigation(articleFirst);
          }}
        >
          <ArticlePreview variant={ArticleCardVariant.ROW_LEAD} article={articleFirst} />
        </div>
      )}
      <ul className="space-y-6 xl:space-y-16">
        {(page === 1 ? articlesAfterFirst : articles).map((article) => {
          return (
            <li
              key={article.slug}
              data-slot="article-card-place"
              data-hash-identifier={article.slug ?? undefined}
              onClick={() => {
                handleArticleNavigation(article);
              }}
            >
              <ArticlePreview variant={ArticleCardVariant.ROW} article={article} />
            </li>
          );
        })}
      </ul>
    </>
  );
}
